import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import { Alert } from "@material-ui/lab";
import axios, { AxiosError } from "axios";
import { Form, Formik, FormikHelpers } from "formik";
import React, { Fragment, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import * as Yup from "yup";
import cuideoTheme from "../../containers/themes/defaultTheme";
import { apiEmpleo } from "../../util/ApiEmpleo";
import { FormikTextField } from "../FormikFields";
import useSnacks from "../hooks/useSnacks";
import {
  SmsButtonState,
  TSendSmsApiResponse,
  TSendSmsButtonState,
  TSendSmsFormValues,
} from "./SendSMSButton.types";
import SmsErrorsTable from "./SmsErrorsTable";

const SMSIconButton = styled(IconButton)`
  padding: 0.33rem;
  border: 1px solid #c2d1da;
  .MuiSvgIcon-root {
    color: ${cuideoTheme.palette.primary.light};
  }
  &:hover {
    .MuiSvgIcon-root {
      color: ${cuideoTheme.palette.primary.main};
    }
  }
`;

const StyledDialog = styled(Dialog)`
  &.CMui-sending {
    .MuiDialogContent-root,
    .MuiDialogActions-root {
      pointer-events: none;
      opacity: 0.5;
    }
  }
`;

const StyledFormWrapper = styled.div`
  margin: 0 1rem;
`;

const StyledAlert = styled(Alert)``;

const StyledButton = styled(Button)`
  width: 5.25rem;
  height: 2.5rem;

  .MuiCircularProgress-root {
    color: #888;
  }
`;

export interface ISendSMSProps {
  phones: string[];
  sender: string;
  message?: string;
}

const SendSMSButton: React.FC<ISendSMSProps> = ({
  phones,
  sender,
  message,
}) => {
  const { formatMessage } = useIntl();
  const { snackSuccessMessage } = useSnacks();
  const [state, setState] = useState<TSendSmsButtonState>({
    open: false,
    state: SmsButtonState.CLOSED,
    errors: [],
    initialValues: {
      phone: phones.join(","),
      sender: sender,
      message: "",
    },
  });

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      initialValues: { ...prevState.initialValues, phone: phones.join(",") },
    }));
  }, [phones]);

  const handleButtonClick = () => {
    setState((prevState) => ({
      ...prevState,
      open: true,
      state: SmsButtonState.OPEN,
    }));
  };

  const handleClose = () => {
    setState((prevState) => ({ ...prevState, open: false }));
  };

  const validationSchema = Yup.object({
    sender: Yup.string()
      .required(
        formatMessage({
          id: "SendSMSButton.Debes indicar el remitente",
          defaultMessage: "Debes indicar el remitente",
        })
      )
      .max(
        11,
        formatMessage({
          id: "SendSMSButton.El remitente no puede superar los 11 caracteres",
          defaultMessage: "El remitente no puede superar los 11 caracteres",
        })
      ),
    message: Yup.string()
      .required(
        formatMessage({
          id: "SendSMSButton.Debes incluir un mensaje",
          defaultMessage: "Debes incluir un mensaje",
        })
      )
      .max(
        160,
        formatMessage({
          id: "SendSMSButton.El mensaje debe ser de un máximo de 160 caracteres",
          defaultMessage: "El mensaje debe ser de un máximo de 160 caracteres",
        })
      ),
  });

  const handleFormSubmit = async (
    values: TSendSmsFormValues,
    actions: FormikHelpers<TSendSmsFormValues>
  ) => {
    setState((prevState) => ({ ...prevState, state: SmsButtonState.SENDING }));

    const newData = {
      phone: values.phone,
      sender: values.sender,
      message: values.message,
    };

    try {
      let res: TSendSmsApiResponse = await apiEmpleo.post("actions/sendSMS", {
        ...newData,
      });

      if (res.status === "SUCCESS" && res.sms_sent === 1) {
        setState((prevState) => ({
          ...prevState,
          open: false,
          state: SmsButtonState.CLOSED,
        }));
        snackSuccessMessage(
          formatMessage({
            id: "SendSMSButton.Mensaje SMS enviado con éxito",
            defaultMessage: "Mensaje SMS enviado con éxito",
          })
        );
      } else if (res.status === "SUCCESS") {
        setState((prevState) => ({
          ...prevState,
          open: false,
          state: SmsButtonState.CLOSED,
        }));
        snackSuccessMessage(
          formatMessage(
            {
              id: "SendSMSButton.{num} mensajes SMS enviados con éxito",
              defaultMessage: "{num} mensajes SMS enviados con éxito",
            },
            {
              num: res.sms_sent,
            }
          )
        );
      } else if (res.status === "PARTIAL_SUCCESS") {
        setState((prevState) => ({
          ...prevState,
          state: SmsButtonState.PARTIAL_SUCCESS,
          errors: res.errors,
        }));
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<TSendSmsApiResponse>;
        const newErrors = serverError.response?.data?.errors ?? [];
        setState((prevState) => ({
          ...prevState,
          state: SmsButtonState.ERROR,
          errors: newErrors,
        }));
      } else {
        console.error("Uncaughted error when user was trying to send SMS");
      }
    }
  };

  return (
    <Fragment>
      <Tooltip
        title={
          <FormattedMessage
            id="SendSMSButton.Enviar SMS"
            defaultMessage="Enviar SMS"
          />
        }
      >
        <SMSIconButton
          className="MuiCuideo-SMSButton"
          onClick={handleButtonClick}
          data-testid="send-sms-button"
        >
          <PhoneAndroidIcon />
        </SMSIconButton>
      </Tooltip>
      <StyledDialog
        open={state.open}
        onClose={handleClose}
        className={
          state.state === SmsButtonState.SENDING ? "CMui-sending" : undefined
        }
      >
        <Formik
          initialValues={state.initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          <Form>
            <DialogTitle>
              <FormattedMessage
                id="SendSMSButton.Enviar SMS"
                defaultMessage="Enviar SMS"
              />
            </DialogTitle>
            <DialogContent>
              {state.state === SmsButtonState.ERROR ? (
                <>
                  <StyledAlert severity="error">
                    <FormattedMessage
                      id="SmsButton.Los contactos presentados a continuación no han recibido el mensaje debido a los siguientes motivos"
                      defaultMessage="Los contactos presentados a continuación no han recibido el mensaje debido a los siguientes motivos"
                    />
                  </StyledAlert>
                  <SmsErrorsTable errors={state.errors} />
                </>
              ) : state.state === SmsButtonState.PARTIAL_SUCCESS ? (
                <>
                  <StyledAlert severity="warning">
                    <FormattedMessage
                      id="SmsButton.Los contactos presentados a continuación no han recibido el mensaje debido a los siguientes motivos"
                      defaultMessage="Los contactos presentados a continuación no han recibido el mensaje debido a los siguientes motivos"
                    />
                  </StyledAlert>
                  <SmsErrorsTable errors={state.errors} />
                </>
              ) : (
                <StyledFormWrapper>
                  <FormikTextField
                    name="phone"
                    variant="outlined"
                    label={
                      <FormattedMessage
                        id="SendSMS.Teléfono"
                        defaultMessage="Teléfono"
                      />
                    }
                    margin="normal"
                    fullWidth
                    disabled
                  />
                  <FormikTextField
                    name="sender"
                    variant="outlined"
                    label={
                      <FormattedMessage
                        id="SendSMS.Remitente"
                        defaultMessage="Remitente"
                      />
                    }
                    margin="normal"
                    fullWidth
                  />
                  <FormikTextField
                    name="message"
                    variant="outlined"
                    multiline
                    label={
                      <FormattedMessage
                        id="SendSMS.Mensaje"
                        defaultMessage="Mensaje"
                      />
                    }
                    margin="normal"
                    fullWidth
                  />
                </StyledFormWrapper>
              )}
            </DialogContent>
            <DialogActions>
              {state.state === SmsButtonState.ERROR ||
              state.state === SmsButtonState.PARTIAL_SUCCESS ? (
                <Button
                  onClick={handleClose}
                  color="primary"
                  variant="contained"
                >
                  <FormattedMessage
                    id="SendSMSButton.Entendido"
                    defaultMessage="Entendido"
                  />
                </Button>
              ) : (
                <>
                  <Button onClick={handleClose} color="primary">
                    <FormattedMessage
                      id="SendSMS.Cancelar"
                      defaultMessage="Cancelar"
                    />
                  </Button>
                  <StyledButton
                    color="primary"
                    variant="contained"
                    type="submit"
                    disabled={state.state === SmsButtonState.SENDING}
                  >
                    {state.state === SmsButtonState.SENDING ? (
                      <CircularProgress size={20} thickness={5} disableShrink />
                    ) : (
                      <FormattedMessage
                        id="SendSMS.Enviar"
                        defaultMessage="Enviar"
                      />
                    )}
                  </StyledButton>
                </>
              )}
            </DialogActions>
          </Form>
        </Formik>
      </StyledDialog>
    </Fragment>
  );
};

export default SendSMSButton;
